import { useEffect, useRef, useState, type JSX } from 'react';
import { MIN_SWIPE_DISTANCE, PERCENTAGE } from './Carousel.constants';
import styles from './Carousel.module.css';
import type { CarouselProps } from './Carousel.types';

const Carousel = ({
  images,
  altPrefix = 'Image',
  startIndex = 0,
  onIndexChange,
  onImageClick,
}: CarouselProps): JSX.Element => {
  const [currentImageIndex, setCurrentImageIndex] =
    useState<number>(startIndex);
  const [touchStart, setTouchStart] = useState<number>(0);
  const [touchEnd, setTouchEnd] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [translateX, setTranslateX] = useState<number>(0);
  const isMovedRef = useRef<boolean>(false);

  useEffect(() => {
    setCurrentImageIndex(startIndex);
  }, [startIndex]);

  const updateIndex = (newIndex: number): void => {
    setCurrentImageIndex(newIndex);
    onIndexChange?.(newIndex);
  };

  const handleTouchStart = (e: React.TouchEvent): void => {
    if (images.length <= 1) return;
    setTouchEnd(0);
    setTouchStart(e.targetTouches[0].clientX);
    setIsDragging(true);
    setTranslateX(0);
    isMovedRef.current = false;
  };

  const handleTouchMove = (e: React.TouchEvent): void => {
    if (!isDragging || images.length <= 1) return;
    const currentTouch = e.targetTouches[0].clientX;
    const diff = touchStart - currentTouch;

    if (Math.abs(diff) > 5) {
      isMovedRef.current = true;
    }

    const isFirstImage = currentImageIndex === 0;
    const isLastImage = currentImageIndex === images.length - 1;
    if ((isFirstImage && diff < 0) || (isLastImage && diff > 0)) return;

    setTranslateX(-diff);
    setTouchEnd(currentTouch);
  };

  const handleTouchEnd = (): void => {
    if (!touchStart || !touchEnd || images.length <= 1) {
      setIsDragging(false);
      setTranslateX(0);
      return;
    }

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > MIN_SWIPE_DISTANCE;
    const isRightSwipe = distance < -MIN_SWIPE_DISTANCE;

    if (isLeftSwipe && currentImageIndex < images.length - 1) {
      updateIndex(currentImageIndex + 1);
    }
    if (isRightSwipe && currentImageIndex > 0) {
      updateIndex(currentImageIndex - 1);
    }

    setIsDragging(false);
    setTranslateX(0);
  };

  const handleImageClick = (index: number): void => {
    // Only trigger if we haven't moved much (not a swipe)
    if (!isMovedRef.current) {
      onImageClick?.(index);
    }
  };

  const handleMouseDown = (e: React.MouseEvent): void => {
    if (images.length <= 1) return;
    setTouchEnd(0);
    setTouchStart(e.clientX);
    setIsDragging(true);
    setTranslateX(0);
    isMovedRef.current = false;
  };

  const handleMouseMove = (e: React.MouseEvent): void => {
    if (!isDragging || images.length <= 1) return;
    const diff = touchStart - e.clientX;

    if (Math.abs(diff) > 5) {
      isMovedRef.current = true;
    }

    const isFirstImage = currentImageIndex === 0;
    const isLastImage = currentImageIndex === images.length - 1;
    if ((isFirstImage && diff < 0) || (isLastImage && diff > 0)) return;

    setTranslateX(-diff);
    setTouchEnd(e.clientX);
  };

  const handleMouseUp = (): void => {
    if (!touchStart || !touchEnd || images.length <= 1) {
      setIsDragging(false);
      setTranslateX(0);
      return;
    }

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > MIN_SWIPE_DISTANCE;
    const isRightSwipe = distance < -MIN_SWIPE_DISTANCE;

    if (isLeftSwipe && currentImageIndex < images.length - 1) {
      updateIndex(currentImageIndex + 1);
    }
    if (isRightSwipe && currentImageIndex > 0) {
      updateIndex(currentImageIndex - 1);
    }

    setIsDragging(false);
    setTranslateX(0);
  };

  return (
    <div className={styles.carouselContainer}>
      <div
        className={`${styles.carouselWrapper} ${
          isDragging ? styles.grabbing : styles.grab
        }`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onContextMenu={(e) => e.preventDefault()}
      >
        <div
          className={`${styles.carouselTrack} ${isDragging ? styles.noTransition : ''}`}
          style={{
            width: `${images.length * PERCENTAGE}%`,
            transform: `translateX(${
              -currentImageIndex * (PERCENTAGE / images.length)
            }%) translateX(${translateX}px)`,
          }}
        >
          {images.map((imageUrl, index) => (
            <div
              key={index}
              className={styles.carouselSlide}
              style={{ width: `${PERCENTAGE / images.length}%` }}
              onClick={() => handleImageClick(index)}
            >
              <img
                src={imageUrl}
                alt={`${altPrefix} ${index + 1}`}
                className={styles.carouselImage}
                draggable={false}
              />
            </div>
          ))}
        </div>
      </div>

      {images.length > 1 && (
        <div className={styles.carouselDots}>
          {images.map((_, index) => (
            <div
              key={index}
              className={`${styles.dot} ${
                index === currentImageIndex ? styles.activeDot : ''
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Carousel;
